import React, { useState } from 'react';
import styled from 'styled-components';
import SimpleReactValidator from 'simple-react-validator';
import { RectangleSkeletonLoader } from '../SkeletonLoader/RectangleSkeletonLoader';
type Props = {
  onChange?: React.ChangeEventHandler<HTMLInputElement>;
  value?: string | number;
  placeholder?: string;
  label?: string;
  type?: string;
  style?: React.CSSProperties | undefined;
  name?: string;
  className?: string;
  disabled?: boolean;
  inputClassName?: string;
  validator?: SimpleReactValidator;
  validations?: string;
  maxLength?: number | undefined;
  onKeyDown?: React.KeyboardEventHandler<HTMLInputElement>;
  onKeyUp?: React.KeyboardEventHandler<HTMLInputElement> | undefined;
  hideStar?: boolean;
  isLoader?: boolean;
  validationLabel?: string;
  defaultValue?: string;
  autoFocus?: boolean;
  min?: number;
  max?: number;
  isReadOnly?: boolean;
  onBlur?: React.FocusEventHandler<HTMLInputElement> | undefined;
  customErrorMsg?: boolean;
  customErrorMsgText?: string;
  customValidationMsg?: any;
  is5digitDecimal?: boolean;
  onPaste?: React.ClipboardEventHandler<HTMLInputElement>;
};
export const TextField = ({
  onChange,
  value,
  placeholder,
  label,
  type,
  style,
  name,
  className,
  disabled,
  inputClassName,
  validator,
  validations,
  maxLength,
  onKeyDown,
  onKeyUp,
  hideStar,
  isLoader,
  validationLabel,
  defaultValue,
  autoFocus,
  min,
  max,
  isReadOnly,
  onBlur,
  onPaste,
  customErrorMsg,
  customErrorMsgText,
  customValidationMsg,
  is5digitDecimal,
}: Props) => {
  const simpleValidator = React.useRef(validator);
  const [validateField, setValidateField] = useState(false);

  // Function to handle input restriction only when the type is "number"
  function handleInputChange(e: React.ChangeEvent<HTMLInputElement>) {
    let inputValue = e.target.value;

    // Apply validation only if the type is "number"
    if (type === 'number') {
      // Regular expression for valid input: max 16 digits before decimal, max 2 digits after decimal
      const regex = is5digitDecimal
        ? /^(\d{0,15}(\.\d{0,5})?)?$/
        : /^(\d{0,15}(\.\d{0,4})?)?$/;

      // If input matches the regex, update the value
      if (regex.test(inputValue)) {
        onChange?.(e);
      }
    } else if (type === 'text') {
      // For text type, prevent leading spaces
      if (inputValue.length > 0 && inputValue[0] === ' ') {
        inputValue = inputValue.trimStart();
      }
      e.target.value = inputValue;
      onChange?.(e);
    } else {
      // For other types, allow any input (or you can add other specific rules)
      onChange?.(e);
    }
  }

  const handleBlur = e => {
    e.preventDefault();

    if (type === 'text' && e?.target?.value) {
      const trimmedValue = e.target.value.trimEnd();
      if (trimmedValue !== e.target.value) {
        e.target.value = trimmedValue;
        onChange?.(e);
      }
    }

    if (!e?.target?.value && validations) {
      simpleValidator?.current?.showMessageFor(
        validationLabel?.toString()!
          ? validationLabel?.toString()!
          : label?.toString()!
          ? label?.toString()!
          : 'field',
      );
      setValidateField(!validateField);
    }
    onBlur?.(e);
  };

  const errorMessage = simpleValidator?.current?.message(
    validationLabel?.toString()
      ? validationLabel?.toString()
      : label?.toString()
      ? label?.toString()
      : 'field',
    value?.toString(),
    validations ?? '',
  );

  // Prevent scientific notation input for number type
  function preventScientificNotation(e: React.KeyboardEvent<HTMLInputElement>) {
    if (type === 'number') {
      // Remove 'e', 'E', '+', '-'
      const invalidKeys = ['e', 'E', '+', '-'];
      if (invalidKeys.includes(e.key)) {
        e.preventDefault();
      }
    }
    onKeyDown?.(e);
  }

  return (
    <InputWrapper className={`${className}`}>
      <div className="label">
        {label}{' '}
        <span className="red-star">{validations && !hideStar ? '*' : ''}</span>
      </div>
      <div style={{ width: '100%' }}>
        {isLoader ? (
          <RectangleSkeletonLoader
            className="field-skeleton"
            count={1}
            height={40}
          />
        ) : (
          <input
            title={
              disabled
                ? defaultValue
                  ? String(defaultValue)
                  : String(value)
                : ''
            }
            placeholder={disabled ? '' : placeholder}
            value={value}
            onChange={handleInputChange} // Use the custom input change handler
            type={type}
            style={style}
            name={name}
            disabled={disabled}
            className={`${inputClassName} ${isReadOnly ? 'read-only' : ''}`}
            onBlur={handleBlur}
            maxLength={maxLength}
            onKeyDown={preventScientificNotation}
            onKeyUp={onKeyUp}
            defaultValue={defaultValue}
            autoFocus={autoFocus}
            min={min?.toString()}
            max={max?.toString()}
            readOnly={isReadOnly}
            onPaste={onPaste}
            onWheel={e => e.currentTarget.blur()}
          />
        )}
        <Errortext className="error-text">
          {errorMessage ? (
            simpleValidator?.current?.message(
              validationLabel?.toString()
                ? validationLabel?.toString()
                : label?.toString()
                ? label?.toString()
                : 'field',
              value?.toString() && value?.toString(),
              validations ?? '',
              customValidationMsg,
            )
          ) : (
            <>{customErrorMsg && customErrorMsgText}</>
          )}
        </Errortext>
      </div>
    </InputWrapper>
  );
};

const InputWrapper = styled.div`
  &.add-payment-component {
    min-height: 90px;
    .label {
      font-size: 12px;
      font-family: 'Open Sans';
      font-weight: 600;
      color: ${p => p.theme.addPaymentLabeltClr};
      margin-left: 0px;
    }
  }
  &.edit-loan-component {
    min-height: 90px;
    @media (max-width: 768px) {
      min-height: 120px;
    }
  }
  &.add-facility-component {
    min-height: 90px;
  }
  &.add-sub-limit-component {
    min-height: 90px;
  }

  .srv-validation-message {
    color: ${p => p.theme.errorStarRed};
    font-size: 13px;
    display: inline-block;
  }
  .red-star {
    color: ${p => p.theme.errorStarRed};
    font-weight: 400;
  }
  :has(input:disabled) {
    .red-star {
      display: none !important;
    }
  }
  input {
    background-color: ${p => p.theme.white};
    border: 1px solid ${p => p.theme.datePickerBorderClr};
    height: 40px;
    width: 100%;
    padding: 0 20px 0 18px;
    border-radius: 5px;
    text-size-adjust: 100%;
    color: ${p => p.theme.datePickerNonActiveInputClr};
    font-family: 'Open Sans';
    font-size: 12px;
    text-overflow: ellipsis;
    overflow: hidden;
    font-weight: 600;
    line-height: 18.15px;
    appearance: textfield; /* Hides spin buttons in Firefox */
    &.add-payment-component-input {
      background-color: ${p => p.theme.addPaymentInputClr};
      border: unset;
    }
    &.read-only {
      background-color: ${p => p.theme.disabledBgClr}!important;
    }
    ::placeholder {
      color: ${p => p.theme.datePickerNonActiveInputClr};
      font-weight: 400;
      font-size: 12px;
      font-family: 'Open Sans';
    }
    :focus-visible {
      outline: unset !important;
    }
    :disabled {
      opacity: 1;
      cursor: default;
      border-color: ${p => p.theme.disabledBrdrClr} !important;
      background-color: ${p => p.theme.disabledBgClr}!important;
      color: ${p => p.theme.textFieldDisableTextColor};
      font-family: 'Open Sans';
    }
  }
  /* Chrome, Safari, Edge, Opera */
  input::-webkit-outer-spin-button,
  input::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
  .label {
    font-family: 'Open Sans';
    color: ${p => p.theme.textFieldTextColor};
    margin-bottom: 5px;
    white-space: nowrap;
    font-size: 14px;
    font-weight: 400;
    line-height: 19.6px;
    margin-left: 4px;
  }
`;

const Errortext = styled.span`
  display: block;
  font-size: 13px !important;
  color: ${p => p.theme.errorStarRed};
  padding-top: 4px !important;
  text-align: left;
  font-family: 'Open Sans';
`;
